import type { GetServerSideProps } from 'next';
import type { BlogPageProps } from 'lib/queries/blog';
import React from 'react';
import dynamic from 'next/dynamic';
import { Wrap } from '@leafwell/components';
import MedicalCardHighlight from 'components/medical-card-highlight';
import CategoriesNavbar from 'components/categories-navbar';
import Archive from 'components/archive';
import PageWrap from 'components/page-wrap';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { getBlogPage } from 'lib/queries/blog';

const CardsGrid = dynamic(() => import('components/cards-grid'));
const NewsletterForm = dynamic(() => import('components/newsletter-form'), {
  ssr: false,
  loading: () => {
    //to prevent layout shift
    return (
      <Wrap
        id="newsletter-signup"
        size="large"
        className="h-[494px] sm:h-[512px] md:h-[330px] lg:h-[272px]"
      />
    );
  },
});

export const getServerSideProps = (async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59',
  );

  return await getBlogPage(context);
}) satisfies GetServerSideProps<BlogPageProps>;

const Blog: React.FC<BlogPageProps> = ({
  page: {
    seo,
    title,
    acf: { pageType },
  },
  categories,
  latestNews,
  postsList: { posts, pagination },
  stickyPosts,
  showFullPage,
}) => {
  const { translations, blogMedicalCardCta } = useWordPressContext();
  const medicalCardProps = {
    description: blogMedicalCardCta.description,
    image: blogMedicalCardCta.image,
    title: blogMedicalCardCta.title,
    variant: blogMedicalCardCta.variant,
    imageAlignment: blogMedicalCardCta.imageAlignment,
    backgroundColor: blogMedicalCardCta.backgroundColor,
  };

  return (
    <PageWrap seo={seo} title={title} pageType={pageType}>
      <CategoriesNavbar categories={categories} />
      {showFullPage ? (
        <>
          {stickyPosts && stickyPosts.length > 0 && (
            <Wrap size="large">
              <CardsGrid items={stickyPosts} variant="featured" />
            </Wrap>
          )}
          {latestNews && latestNews.length > 0 && (
            <Wrap size="large">
              <CardsGrid
                items={latestNews}
                title={translations['Latest news']}
                variant="small"
              />
            </Wrap>
          )}
          <MedicalCardHighlight {...medicalCardProps} />
        </>
      ) : null}
      <Archive
        title={translations['Articles']}
        posts={posts}
        pagination={pagination}
      />
      <NewsletterForm />
    </PageWrap>
  );
};

export default Blog;
